import _ from 'underscore';
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { filterQuery } from '@culture/common-helpers/url';

import Button from 'components/filters/Button';
import CustomSelect from 'components/filters/CustomSelect';
import Popup from 'components/Popup';
import SimpleSearch from 'components/filters/SimpleSearch';
import Switch from 'components/filters/Switch';

import { filtersValuesPropType } from 'pageTemplates/institutes/propTypes';

import SortFilters from 'components/filters/SortFilters';
import ClearButton from 'components/filters/ClearButton';
import styles from './styles.module.scss';

const renderMuseumsFilters = (
	filters,
	isOpenMore,
	togglePopupMore,
	refMore,
	onFilterChange
) => {
	const museumsSwitchFilters = [
		{ name: 'isCommercial', label: 'Частные музеи' },
		{ name: 'hasVirtualTours', label: 'Виртуальные музеи' },
		{ name: 'hasInfographic', label: 'Инфографика' }
	];

	const museumsFilters = museumsSwitchFilters.reduce((acc, curr) => {
		if (filters[curr.name]) {
			acc.push(curr.label);
		}
		return acc;
	}, []);

	const title = _.isEmpty(museumsFilters) ? 'Еще' : museumsFilters.join(', ');
	const maxTitleLength = 20;
	const showTooltip = title.length > maxTitleLength;

	const submitAndClose = (name, value) => {
		onFilterChange(name, value);
		togglePopupMore();
	};

	return (
		<>
			<Button
				title={title}
				tooltip={showTooltip ? title : ''}
				marker={isOpenMore ? 'up' : 'down'}
				ref={refMore}
				reverse
				onClick={togglePopupMore}
				theme="bright"
				icon="arrow-down"
				className={cx(styles.DesktopInstituteControls__MoreButton, {
					[styles.DesktopInstituteControls__MoreButton_selected]: isOpenMore
				})}
				iconClassName={cx({
					[styles.DesktopInstituteControls__IconUp]: isOpenMore,
					[styles.DesktopInstituteControls__IconBottom]: !isOpenMore
				})}
			/>
			<Popup
				isOpen={isOpenMore}
				onClickOutside={togglePopupMore}
				element={refMore.current}
				placement="bottom-start"
				options={{
					strategy: 'absolute',
					modifiers: [
						{
							name: 'flip',
							options: {
								fallbackPlacements: ['bottom']
							}
						}
					]
				}}
			>
				<div className={styles.DesktopInstituteControls__ListSwitch}>
					{museumsSwitchFilters.map((filter) => (
						<Switch
							key={filter.name}
							name={filter.name}
							title={filter.label}
							theme="simple"
							checked={filters?.[filter.name]}
							onChange={submitAndClose}
						/>
					))}
				</div>
			</Popup>
		</>
	);
};

const DesktopInstituteControls = ({
	defaultFiltersValues,
	filters,
	filtersValues,
	onFilterChange,
	onFilterReset,
	rubricPath,
	showSortFilter
}) => {
	const refMore = useRef(null);
	const [isOpenMore, setIsOpenMore] = useState(false);

	const prepareDefaultValueFilters = _(filters).mapObject((val, key) => {
		if (
			(key === 'location' && val === 'russia') ||
			['sort', 'cleanCache'].includes(key)
		) {
			return null;
		}

		return val;
	});

	const showGlobalButtonClear = !_(
		filterQuery(prepareDefaultValueFilters)
	).isEmpty();

	const togglePopupMore = () => {
		setIsOpenMore((state) => !state);
	};

	return (
		<div className={styles.DesktopInstituteControls}>
			<div className={styles.DesktopInstituteControls__Block}>
				<CustomSelect
					controlTitleField="title"
					name="location"
					options={filtersValues.locales}
					onChange={onFilterChange}
					resetOption={{
						title: 'По всей России',
						sysName: defaultFiltersValues.location,
						titleGenitive: 'России'
					}}
					searchable
					selectedValue={filters.location}
					selectedOption={filtersValues?.currentLocale}
					valueField="sysName"
				/>
				{rubricPath === 'museums/institutes' &&
					renderMuseumsFilters(
						filters,
						isOpenMore,
						togglePopupMore,
						refMore,
						onFilterChange
					)}
				{rubricPath === 'architecture/institutes' && (
					<CustomSelect
						apiParams={{
							url: 'persons',
							dataPath: 'items',
							debounce: 300,
							affectQueryParams: {
								_ids: !_.isEmpty(filtersValues?.architectsIds)
									? filtersValues?.architectsIds
									: null,
								sort: 'surname,title',
								fields: ['title', 'name'].join(),
								limit: 100
							},
							paginationType: 'pagination'
						}}
						controlTitleField="title"
						name="architect"
						label="Архитектор"
						options={filtersValues.architects}
						onChange={onFilterChange}
						resetOption={{
							title: 'Все архитекторы',
							name: ''
						}}
						searchable
						selectedValue={filters?.architect}
						valueField="name"
						titleClassName={styles.title}
					/>
				)}
				{rubricPath === 'literature/libraries' && (
					<>
						<CustomSelect
							controlTitleField="title"
							name="subordinationType"
							label="Тип подчинения"
							options={filtersValues?.subordinationTypes}
							onChange={onFilterChange}
							selectedValue={filters?.subordinationType}
							valueField="name"
							titleClassName={styles.title}
						/>
						<Switch
							name="isModelLibrary"
							title="Модельная библиотека"
							theme="simple"
							checked={filters?.isModelLibrary}
							onChange={onFilterChange}
						/>
					</>
				)}
				{rubricPath === 'music/virtual-concert-halls' && (
					<>
						<CustomSelect
							controlTitleField="title"
							name="type"
							label="Тип"
							options={filtersValues?.instituteTypes}
							onChange={onFilterChange}
							selectedValue={filters?.type}
							valueField="name"
							titleClassName={styles.title}
						/>
					</>
				)}
				{showGlobalButtonClear && <ClearButton onClick={onFilterReset} />}
			</div>
			<div className={styles.DesktopInstituteControls__Block}>
				{!!showSortFilter && (
					<SortFilters
						onFilterChange={onFilterChange}
						currentSort={filters.sort}
					/>
				)}
				<SimpleSearch
					onEnter={onFilterChange}
					placeholder="Поиск"
					value={filters.query}
				/>
			</div>
		</div>
	);
};

DesktopInstituteControls.propTypes = {
	defaultFiltersValues: PropTypes.shape({
		location: PropTypes.string
	}).isRequired,
	filters: PropTypes.shape({
		hasInfographic: PropTypes.bool,
		hasVirtualTours: PropTypes.bool,
		isCommercial: PropTypes.bool,
		location: PropTypes.string,
		query: PropTypes.string,
		sort: PropTypes.string,
		architect: PropTypes.string,
		type: PropTypes.string,
		subordinationType: PropTypes.string,
		isModelLibrary: PropTypes.bool
	}),
	filtersValues: filtersValuesPropType,
	onFilterChange: PropTypes.func,
	onFilterReset: PropTypes.func,
	rubricPath: PropTypes.string,
	showSortFilter: PropTypes.bool
};

DesktopInstituteControls.defaultProps = {
	onFilterReset: _.noop,
	filters: {},
	filtersValues: []
};

export default DesktopInstituteControls;
